import { MapPin, ArrowUpRight } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { useLang } from "../context/LanguageContext";
import { LEGAL_COPY } from "../lib/legalCopy";

export const Footer = () => {
  const { t, lang } = useLang();
  const year = new Date().getFullYear();
  const legal = (LEGAL_COPY[lang] || LEGAL_COPY.pt).footer;

  const serviceLinks = [
    { slug: "assistencia-tecnica", title: t.services.assistance.title },
    { slug: "televisao", title: t.services.television.title },
    { slug: "comunicacoes", title: t.services.communications.title },
    { slug: "informatica", title: t.services.it.title },
  ];

  const navLinks = [
    { href: "/#about", label: t.nav.about, type: "anchor", id: "about" },
    { href: "/equipamentos", label: t.nav.equipment, type: "route", id: "equipment" },
    { href: "/portfolio", label: t.nav.portfolio, type: "route", id: "portfolio" },
    { href: "/#services", label: t.nav.services, type: "anchor", id: "services" },
    { href: "/#partners", label: t.nav.partners, type: "anchor", id: "partners" },
    { href: "/#whyus", label: t.nav.whyus, type: "anchor", id: "whyus" },
    { href: "/#contact", label: t.nav.contact, type: "anchor", id: "contact" },
  ];

  return (
    <footer
      data-testid="site-footer"
      className="relative overflow-hidden border-t border-slate-300 bg-[#90b5d3] text-slate-800"
    >
      <div className="mx-auto max-w-[1400px] px-6 pb-10 pt-20 md:px-12 lg:px-16">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12">
          <div className="md:col-span-5">
            <div className="flex items-center">
              <img
                src="/logo-tvcabsat.png"
                alt="TVCabSat"
                className="h-24 w-auto object-contain md:h-32"
              />
            </div>
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-slate-600">
              {t.footer.tagline}
            </p>
            <div className="mt-6 flex flex-col gap-2 text-sm text-slate-700">
              <div className="flex items-center gap-2">
                <MapPin size={14} className="text-blue-600" />
                <span>Rua Padre Manuel Ruela Pombo, 40 · R/C e 3º andar · Maianga</span>
              </div>
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
              {t.footer.nav_title}
            </div>
            <ul className="mt-5 space-y-3">
              {navLinks.map((l) =>
                l.type === "route" ? (
                  <li key={l.href}>
                    <Link
                      to={l.href}
                      data-testid={`footer-nav-${l.id}`}
                      className="group inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
                    >
                      {l.label}
                      <ArrowUpRight
                        size={12}
                        weight="bold"
                        className="opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100"
                      />
                    </Link>
                  </li>
                ) : (
                  <li key={l.href}>
                    <a
                      href={l.href}
                      data-testid={`footer-nav-${l.id}`}
                      className="group inline-flex items-center gap-1 text-sm text-slate-600 hover:text-slate-900"
                    >
                      {l.label}
                      <ArrowUpRight
                        size={12}
                        weight="bold"
                        className="opacity-0 transition-all group-hover:translate-x-0.5 group-hover:opacity-100"
                      />
                    </a>
                  </li>
                ),
              )}
            </ul>
          </div>

          <div className="md:col-span-4">
            <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">
              {t.footer.services_title}
            </div>
            <ul className="mt-5 space-y-3">
              {serviceLinks.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/servicos/${s.slug}`}
                    data-testid={`footer-service-${s.slug}`}
                    className="text-sm text-slate-600 hover:text-slate-900"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Empresas Grupo TVCabsat — placed ABOVE the divider line.
            mix-blend-mode: lighten lets the black background bundled in the
            source PNGs visually disappear over the light footer surface, so
            the logos read clean without a dark wrapper card. */}
        <div
          data-testid="footer-group"
          className="mt-14 flex flex-col items-start gap-4 md:flex-row md:items-center md:justify-end"
        >
          <div className="font-mono text-[11px] font-black uppercase tracking-[0.25em] text-slate-900 md:mr-4">
            {t.footer.group_title}
          </div>
          <div className="flex flex-wrap items-center gap-5 md:gap-7">
            <div
              data-testid="group-dauag"
              className="flex items-center transition-transform hover:-translate-y-0.5"
              title="DAuAg — Grupo TVCabsat"
            >
              <img
                src="/group/dauag-transparent.png"
                alt="DAuAg — Grupo TVCabsat"
                loading="lazy"
                className="h-20 w-20 shrink-0 object-contain md:h-24 md:w-24"
              />
              <div className="-ml-3 text-left md:-ml-4">
                <div className="font-heading text-base font-medium leading-none text-slate-900">
                  DAuAg
                </div>
                <div className="mt-1.5 max-w-[180px] text-[11px] font-bold leading-tight text-slate-700">
                  {t.footer.group_dauag_desc}
                </div>
              </div>
            </div>

            <div
              data-testid="group-wesa"
              className="flex items-center gap-1.5 transition-transform hover:-translate-y-0.5"
              title="Wesa Moxi Net — Grupo TVCabsat"
            >
              <img
                src="/group/wesamoxi-net-transparent.png"
                alt="Wesa Moxi Net — Grupo TVCabsat"
                loading="lazy"
                className="h-20 w-20 shrink-0 object-contain md:h-24 md:w-24"
              />
              <div className="text-left">
                <div className="font-heading text-base font-medium leading-none text-slate-900">
                  Wesa Moxi Net
                </div>
                <div className="mt-1.5 max-w-[180px] text-[11px] font-bold leading-tight text-slate-700">
                  {t.footer.group_wesa_desc}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 flex flex-col gap-4 border-t border-slate-200 pt-6 md:flex-row md:items-center md:justify-between">
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-slate-500">
            © {year} TVCabSat, Lda · {t.footer.rights}
          </div>
          <nav
            aria-label="Legal"
            className="flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600"
            data-testid="footer-legal-links"
          >
            <Link
              to="/politica-privacidade"
              data-testid="footer-link-privacy"
              className="transition hover:text-blue-700"
            >
              {legal.privacy}
            </Link>
            <span className="opacity-40">·</span>
            <Link
              to="/termos"
              data-testid="footer-link-terms"
              className="transition hover:text-blue-700"
            >
              {legal.terms}
            </Link>
            <span className="opacity-40">·</span>
            <span className="text-slate-500">Luanda · Angola</span>
          </nav>
        </div>
      </div>
    </footer>
  );
};
